/**
 * Audience filter panel: collapsible sections of chip toggles that own the wizard's filter selections.
 */

import React from 'react';
import { Box, Collapse, Switch, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  affinitiesForInterests,
  AGE_OPTIONS,
  DEVICE_OPTIONS,
  DEVICE_TIER_OPTIONS,
  EXCLUSION_ITEMS,
  GENDER_OPTIONS,
  INTEREST_CATEGORIES,
  OS_OPTIONS,
  OWNED_CHANNELS,
} from '../constants';
import type { AudienceFiltersProps, FilterSelections } from '../types';

// Filter panel
// Inline Styles
// Filter panel
// Inline Styles
const FSEC_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '14px 16px',
  cursor: 'pointer',
  backgroundColor: '#fff',
  justifyContent: 'space-between',
  '&:hover': { backgroundColor: '#fafafa' },
};

const FSEC_TITLE_SX = {
  fontSize: 13,
  fontWeight: 700,
  color: '#111',
  fontFamily: 'inherit',
};

const CHEVRON_SX = (isExpanded: boolean) => ({
  fontSize: 18,
  color: '#666',
  transition: 'transform 0.2s',
  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
});

const CHIP_ROW_SX = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: '6px',
};

const CHIP_SX = (selected: boolean) => ({
  fontSize: 11,
  fontWeight: 600,
  padding: '5px 11px',
  borderRadius: '7px',
  border: '1px solid',
  borderColor: selected ? '#1c9dea' : '#e0e0e0',
  backgroundColor: selected ? '#1c9dea' : '#fff',
  color: selected ? '#fff' : '#666',
  cursor: 'pointer',
  transition: 'all 0.12s',
  fontFamily: 'inherit',
  '&:hover': {
    borderColor: selected ? '#1c9dea' : '#c0cfe8',
    backgroundColor: selected ? '#1c9dea' : '#f5f8ff',
    color: selected ? '#fff' : '#1c9dea',
  },
});

const CHIP_DISABLED_SX = {
  ...CHIP_SX(false),
  backgroundColor: '#fafafa',
  borderColor: '#eee',
  color: '#bbb',
  cursor: 'not-allowed',
  '&:hover': {
    backgroundColor: '#fafafa',
    borderColor: '#eee',
    color: '#bbb',
  },
};

const COMING_SOON_SX = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: '#9a3412',
  backgroundColor: '#fff7ed',
  padding: '1px 6px',
  borderRadius: '4px',
  fontFamily: 'inherit',
};

const SUB_LABEL_SX = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  color: '#aaa',
  mb: '5px',
  fontFamily: 'inherit',
};

// Section wrapper

// Section wrapper
const FilterSection: React.FC<{
  index: number;
  title: string;
  summary: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ index, title, summary, expanded, onToggle, children }) => (
  <Box
    sx={{
      backgroundColor: '#fff',
      border: '1px solid #ebebeb',
      borderRadius: '12px',
      mb: 2,
      overflow: 'hidden',
    }}
  >
    <Box sx={FSEC_HEADER_SX} onClick={onToggle}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: '#1c9dea',
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {index}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={FSEC_TITLE_SX}>{title}</Typography>
          {!expanded && (
            <Typography
              sx={{
                fontSize: 11,
                color: '#999',
                mt: '2px',
                fontFamily: 'inherit',
              }}
            >
              {summary}
            </Typography>
          )}
        </Box>
      </Box>
      <ExpandMoreIcon sx={CHEVRON_SX(expanded)} />
    </Box>
    <Collapse in={expanded}>
      <Box sx={{ p: '0 16px 16px', borderTop: '1px solid #f5f5f5' }}>
        {children}
      </Box>
    </Collapse>
  </Box>
);

// Component
/**
 * Step 3 filter panel - Demographics, Device type, Interests & Affinity,
 * Exclusions. Geography and naming are intentionally absent: geography comes
 * from Step 2 (audiences + sites), and the wizard's own navigation advances
 * the step.
 */

// Component
/**
 * Step 3 filter panel - Demographics, Device type, Interests & Affinity,
 * Exclusions. Geography and naming are intentionally absent: geography comes
 * from Step 2 (audiences + sites), and the wizard's own navigation advances
 * the step.
 */
const AudienceFilters: React.FC<AudienceFiltersProps> = ({
  selections,
  onChange,
}) => {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({
    demographics: true,
    deviceType: true,
    interests: true,
    exclusions: true,
  });

  const toggleSection = (section: string) =>
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));

  const toggleValue = (field: keyof FilterSelections, value: string) => {
    const current = selections[field];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];

    // Gender is one choice of three - picking Male means only males, and
    // clearing the last pick falls back to All rather than to nothing.
    if (field === 'genders') {
      onChange({
        ...selections,
        genders: next.length === 0 ? ['all'] : [value],
      });
      return;
    }

    // Affinity signals belong to their interest - drop any that the remaining
    // interests no longer offer, so the selection can't outlive its category.
    if (field === 'interests') {
      const offered = affinitiesForInterests(next);
      onChange({
        ...selections,
        interests: next,
        affinities: selections.affinities.filter(a => offered.includes(a)),
      });
      return;
    }

    onChange({ ...selections, [field]: next });
  };

  /** Affinity chips always reflect whatever interests are currently selected. */
  const offeredAffinities = affinitiesForInterests(selections.interests);

  const summarize = (values: string[], empty: string) =>
    values.length > 0
      ? values.slice(0, 3).join(' • ') +
        (values.length > 3 ? ` • +${values.length - 3}` : '')
      : empty;

  const renderChips = (field: keyof FilterSelections, options: string[]) => (
    <Box sx={CHIP_ROW_SX}>
      {options.map(option => (
        <Box
          key={option}
          sx={CHIP_SX(selections[field].includes(option))}
          onClick={() => toggleValue(field, option)}
        >
          {option === 'female'
            ? 'Female'
            : option === 'male'
              ? 'Male'
              : option === 'all'
                ? 'All'
                : option}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* 1 · Demographics */}
      <FilterSection
        index={1}
        title="Demographics"
        summary={summarize(
          [...selections.genders, ...selections.ages],
          'No constraint'
        )}
        expanded={expanded.demographics}
        onToggle={() => toggleSection('demographics')}
      >
        <Typography sx={{ ...SUB_LABEL_SX, mt: 2 }}>Gender</Typography>
        {renderChips('genders', GENDER_OPTIONS)}

        <Box sx={{ height: 1, backgroundColor: '#f0f0f0', my: 2 }} />

        <Typography sx={SUB_LABEL_SX}>Age bracket</Typography>
        {renderChips('ages', AGE_OPTIONS)}
      </FilterSection>

      {/* 2 · Device type */}
      <FilterSection
        index={2}
        title="Device type"
        summary={summarize(
          [...selections.devices, ...selections.os, ...selections.deviceTiers],
          'No constraint'
        )}
        expanded={expanded.deviceType}
        onToggle={() => toggleSection('deviceType')}
      >
        <Typography sx={{ ...SUB_LABEL_SX, mt: 2 }}>Device</Typography>
        {renderChips('devices', DEVICE_OPTIONS)}

        <Box sx={{ height: 1, backgroundColor: '#f0f0f0', my: 2 }} />

        <Typography sx={SUB_LABEL_SX}>OS</Typography>
        {renderChips('os', OS_OPTIONS)}

        <Box sx={{ height: 1, backgroundColor: '#f0f0f0', my: 2 }} />

        <Typography
          sx={{
            ...SUB_LABEL_SX,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Owned channels
          <Box component="span" sx={COMING_SOON_SX}>
            Coming soon
          </Box>
        </Typography>
        <Box sx={CHIP_ROW_SX}>
          {OWNED_CHANNELS.map(channel => (
            <Box key={channel} sx={CHIP_DISABLED_SX}>
              {channel}
            </Box>
          ))}
        </Box>

        <Box sx={{ height: 1, backgroundColor: '#f0f0f0', my: 2 }} />

        <Typography sx={SUB_LABEL_SX}>Device tier</Typography>
        {renderChips('deviceTiers', DEVICE_TIER_OPTIONS)}
      </FilterSection>

      {/* 3 · Interests & Affinity */}
      <FilterSection
        index={3}
        title="Interests & Affinity"
        summary={summarize(
          [...selections.interests, ...selections.affinities],
          'No constraint'
        )}
        expanded={expanded.interests}
        onToggle={() => toggleSection('interests')}
      >
        <Typography sx={{ ...SUB_LABEL_SX, mt: 2 }}>
          Interest categories
        </Typography>
        {renderChips('interests', INTEREST_CATEGORIES)}

        <Box sx={{ height: 1, backgroundColor: '#f0f0f0', my: 2 }} />

        <Typography sx={SUB_LABEL_SX}>
          Affinity signals
          {selections.interests.length > 0 &&
            ` · ${selections.interests.length} categor${
              selections.interests.length === 1 ? 'y' : 'ies'
            }`}
        </Typography>
        {offeredAffinities.length > 0 ? (
          renderChips('affinities', offeredAffinities)
        ) : (
          <Typography
            sx={{ fontSize: 11, color: '#999', fontFamily: 'inherit' }}
          >
            Pick one or more interest categories above — their affinity signals
            appear here.
          </Typography>
        )}
      </FilterSection>

      {/* 4 · Exclusions */}
      <FilterSection
        index={4}
        title="Exclusions"
        summary={
          selections.exclusions.length > 0
            ? `${selections.exclusions.length} active`
            : 'None'
        }
        expanded={expanded.exclusions}
        onToggle={() => toggleSection('exclusions')}
      >
        <Box sx={{ mt: 1 }}>
          {EXCLUSION_ITEMS.map(item => (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: '12px 0',
                borderBottom: '1px solid #f0f0f0',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#111',
                    mb: '2px',
                    fontFamily: 'inherit',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{ fontSize: 11, color: '#999', fontFamily: 'inherit' }}
                >
                  {item.description}
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={selections.exclusions.includes(item.key)}
                onChange={() => toggleValue('exclusions', item.key)}
                sx={{
                  ml: 2,
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#1c9dea' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#1c9dea',
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </FilterSection>
    </Box>
  );
};

// Step: Audience builder
// Bar styles

export default AudienceFilters;
